import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiCalendar, FiZap, FiDollarSign } = FiIcons;

function Projects() {
  const projects = [
    {
      title: "Greenwood Residential Complex",
      location: "California, USA",
      date: "2024",
      capacity: "2.5 MW",
      savings: "$450,000/year",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Large-scale residential solar installation serving 150 homes with clean energy.",
      type: "Residential"
    },
    {
      title: "TechCorp Manufacturing Facility",
      location: "Texas, USA",
      date: "2023",
      capacity: "5.2 MW",
      savings: "$890,000/year",
      image: "https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Commercial rooftop solar system reducing operational costs for manufacturing plant.",
      type: "Commercial"
    },
    {
      title: "Sunnydale Shopping Center",
      location: "Florida, USA",
      date: "2023",
      capacity: "3.8 MW",
      savings: "$650,000/year",
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Retail complex solar installation with integrated battery storage system.",
      type: "Commercial"
    },
    {
      title: "Riverside Community Center",
      location: "Oregon, USA",
      date: "2024",
      capacity: "1.2 MW",
      savings: "$180,000/year",
      image: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Community solar project providing clean energy to local government facilities.",
      type: "Community"
    },
    {
      title: "Ocean View Luxury Homes",
      location: "Hawaii, USA",
      date: "2023",
      capacity: "800 kW",
      savings: "$120,000/year",
      image: "https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Premium residential solar installations for luxury coastal properties.",
      type: "Residential"
    },
    {
      title: "Metro Hospital Solar Array",
      location: "New York, USA",
      date: "2024",
      capacity: "4.5 MW",
      savings: "$780,000/year",
      image: "https://images.unsplash.com/photo-1582719471137-c3967ffb1c42?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      description: "Critical infrastructure solar system ensuring reliable power for healthcare facility.",
      type: "Healthcare"
    }
  ];

  const stats = [
    { label: "Total Projects", value: "500+" },
    { label: "Capacity Installed", value: "50+ MW" },
    { label: "Annual Savings", value: "$5M+" },
    { label: "CO2 Reduced", value: "25,000 tons" }
  ];

  return (
    <>
      <Helmet>
        <title>Solar Projects - SolarTech Solutions | Residential & Commercial Solar Installations</title>
        <meta name="description" content="Explore our completed solar projects including residential, commercial, and community installations. Over 500 successful solar energy projects with 50+ MW capacity." />
        <meta name="keywords" content="solar projects, solar installations, residential solar projects, commercial solar projects, solar portfolio, solar case studies" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Solar Projects</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Discover our portfolio of successful solar installations across residential, commercial, 
              and community sectors. Each project represents our commitment to quality, innovation, 
              and sustainable energy solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center text-white"
              >
                <h3 className="text-3xl md:text-4xl font-bold mb-2">{stat.value}</h3>
                <p className="text-lg opacity-90">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Featured Projects
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore some of our most successful solar installations that showcase our expertise 
              and commitment to delivering exceptional results
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="relative">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {project.type}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">{project.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{project.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <SafeIcon icon={FiMapPin} className="text-orange-500" />
                      <span>{project.location}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <SafeIcon icon={FiCalendar} className="text-orange-500" />
                      <span>{project.date}</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <SafeIcon icon={FiZap} className="text-orange-500 text-sm" />
                        <span className="text-xs text-gray-500">Capacity</span>
                      </div>
                      <p className="font-semibold text-gray-800">{project.capacity}</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <SafeIcon icon={FiDollarSign} className="text-green-500 text-sm" />
                        <span className="text-xs text-gray-500">Savings</span>
                      </div>
                      <p className="font-semibold text-gray-800">{project.savings}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                Why Our Projects Succeed
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our proven methodology and attention to detail ensure every project delivers 
                exceptional results and exceeds client expectations.
              </p>
              
              <div className="space-y-6">
                {[
                  "Comprehensive site assessment and energy analysis",
                  "Custom system design optimized for maximum efficiency",
                  "Professional installation by certified technicians",
                  "Rigorous quality control and testing procedures",
                  "Ongoing monitoring and maintenance support"
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white text-xs font-bold">{index + 1}</span>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Solar installation team"
                className="rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start Your Solar Project?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Join our growing list of satisfied customers who have made the switch to clean, 
              renewable solar energy. Contact us today to discuss your project requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-orange-500 px-8 py-4 rounded-full font-semibold hover:shadow-lg transition-shadow">
                Start Your Project
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-500 transition-colors">
                View All Projects
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default Projects;